import { useQueries } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';

import { accentCss } from '@octbill/shared/colors';

import { api } from '../api.js';
import { useAuth } from '../auth.jsx';
import { useFormatDate } from '../lib/format.js';
import { fill, useT } from '../lib/i18n.js';
import { addonOn, clientNav } from '../modules.js';
import { CookieNotice, SiteLink } from './CookieNotice.jsx';
import { NavItem } from './NavItem.jsx';
import { NotificationBell } from './NotificationBell.jsx';
import { SiteFooterLinks, SiteHeaderMenu, SiteSidebarPages, useSitePages } from './SitePages.jsx';
import { TentacleMark } from './Tentacle.jsx';
import { ThemePicker } from './ThemePicker.jsx';

/**
 * The sidebar: the dashboard, then every module's clientNav (web/src/modules.js). Entries with
 * `auth: true` show only to signed-in clients. Entries fall into the sections below by key (an
 * addon's go to the first); an entry's `badge: { key, load }` shows a count next to it (unpaid
 * invoices, tickets waiting for the client), loaded once a minute.
 */
const DASHBOARD = { key: 'dashboard', to: '/', label: 'Dashboard', auth: true, order: 0 };
// Visitors get the home page at / instead (pages/Home.jsx), unless the admin turned it off.
const HOME = { key: 'home', to: '/', label: 'Home', guest: true, order: 0 };
const NAV_SECTIONS = [
  { key: 'main', label: null },
  { key: 'order', label: 'nav.section.order', keys: ['store', 'cart'] },
  { key: 'help', label: 'nav.section.help', keys: ['knowledgebase', 'announcements', 'network-status'] },
  {
    key: 'account',
    label: 'nav.section.account',
    keys: ['account', 'contacts', 'security', 'emails', 'language', 'api-keys', 'webhooks', 'affiliates'],
  },
];
const sectionOf = (key) => NAV_SECTIONS.find((s) => s.keys?.includes(key))?.key ?? 'main';

/** A nav entry's words: the catalogue's `nav.<key>` (translated), else the module's label. */
function navLabel(t, item) {
  const id = `nav.${item.key.replace(/-/g, '_')}`;
  const text = t(id);
  return text !== id ? text : (item.label ?? item.key);
}

/** The client's sidebar, in sections, with count badges. */
function ClientNav({ items, signedIn }) {
  const t = useT();
  const badged = signedIn ? items.filter((i) => i.badge) : [];
  const counts = useQueries({
    queries: badged.map((i) => ({
      queryKey: ['nav-badge', i.badge.key],
      queryFn: i.badge.load,
      staleTime: 30_000,
      refetchInterval: 60_000,
      refetchIntervalInBackground: false,
      retry: false,
    })),
  });
  const countOf = new Map(badged.map((i, n) => [i.key, Number(counts[n]?.data) || 0]));
  return NAV_SECTIONS.map((section) => {
    const list = items.filter((i) => sectionOf(i.key) === section.key);
    if (!list.length) return null;
    return (
      <div key={section.key} className={section.label ? 'mt-4' : ''}>
        {section.label ? <p className="mb-1 px-3 text-xs font-semibold text-white/60">{t(section.label)}</p> : null}
        {list.map((item) => {
          const label = navLabel(t, item);
          const n = countOf.get(item.key) ?? 0;
          return (
            <NavItem key={item.key} to={item.to} end={item.to === '/'}>
              <span className="flex items-center justify-between gap-2">
                <span className="truncate">{label}</span>
                {n ? (
                  <span className="rounded-full bg-accent px-1.5 text-xs font-semibold text-on-accent tabular-nums">
                    <span aria-hidden="true">{n > 99 ? '99+' : n}</span>
                    <span className="sr-only">{t(item.badge.label, { count: n })}</span>
                  </span>
                ) : null}
              </span>
            </NavItem>
          );
        })}
      </div>
    );
  });
}

/** The admin's brand colours, as the same CSS variables styles.css sets. */
function BrandColors({ branding }) {
  const css = accentCss({ light: branding.accent_light, dark: branding.accent_dark });
  return css ? <style>{css}</style> : null;
}

/** Staff only: maintenance mode is on, so clients can't log in or order. */
function MaintenanceBanner() {
  const t = useT();
  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-amber/50 bg-amber/10 px-4 py-2 text-sm md:px-10"
    >
      <p>{t('shell.maintenance_on')}</p>
      <Link to="/admin/settings/general" className="font-semibold underline">
        {t('shell.maintenance_settings')}
      </Link>
    </div>
  );
}

/** Staff viewing the client area as a client: who, and the way back to the admin area. */
function ImpersonationBanner({ me }) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  async function stop() {
    setBusy(true);
    try {
      const res = await api('/api/auth/impersonation/stop', { method: 'POST' });
      window.location.assign(res?.redirect ?? '/admin');
    } catch {
      setBusy(false);
    }
  }
  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-amber/50 bg-amber/10 px-4 py-2 text-sm md:px-10"
    >
      <p>{fill(t('shell.impersonating'), { name: <strong>{me.client?.name || me.client?.email}</strong> })}</p>
      <button type="button" className="font-semibold underline" onClick={stop} disabled={busy}>
        {t('shell.back_to_admin')}
      </button>
    </div>
  );
}

/** Footer language picker; only shown when more than one language is switched on. */
function LanguagePicker() {
  const { language, languages, setLanguage } = useAuth();
  const t = useT();
  if (!languages || languages.length < 2) return null;
  return (
    <label className="flex items-center gap-2">
      <span>{t('nav.language')}</span>
      <select
        className="rounded border border-line bg-surface px-1 py-0.5 text-xs text-text"
        value={language}
        onChange={(e) => setLanguage(e.target.value)}
      >
        {languages.map((l) => (
          <option key={l.code} value={l.code}>
            {l.name}
          </option>
        ))}
      </select>
    </label>
  );
}

/** App frame: ink sidebar (collapses to a top bar on small screens) + content. */
export function Shell() {
  const { me, company, branding, brand, addons, legal, logout } = useAuth();
  const t = useT();
  useFormatDate(); // so plain date helpers (table cells) use this person's format too
  const [open, setOpen] = useState(false);
  const menuButton = useRef(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // The phone menu closes with Esc (focus goes back to its button) and whenever the page changes.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      menuButton.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  useEffect(() => setOpen(false), [pathname]);

  async function onLogout() {
    await logout();
    navigate('/login');
  }

  const items = [branding.landing_page === false ? null : HOME, DASHBOARD, ...clientNav()].filter(
    (item) => item && (!item.auth || me?.client) && (!item.guest || !me?.client) && addonOn(item, addons),
  );
  const sitePages = useSitePages();

  return (
    <div className="min-h-screen md:grid md:grid-cols-[15rem_1fr]">
      <a
        href="#main"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById('main')?.focus();
        }}
        className="sr-only z-50 rounded-md bg-surface px-3 py-2 text-sm font-semibold text-text shadow focus:not-sr-only focus:fixed focus:top-2 focus:left-2"
      >
        {t('shell.skip')}
      </a>
      <aside
        aria-label={t('shell.sidebar')}
        className="bg-ink text-white md:sticky md:top-0 md:flex md:h-screen md:flex-col"
      >
        <div className="flex items-center justify-between px-4 py-4">
          <Link to="/" className="flex min-w-0 items-center gap-2 font-bold">
            {/* An extra brand (the brands module) shows its own logo instead of the tentacle. */}
            {brand && company.logo_url ? (
              <img src={company.logo_url} alt="" className="h-7 max-w-24 rounded bg-white object-contain p-0.5" />
            ) : (
              <TentacleMark />
            )}
            <span className="truncate">{company.name}</span>
          </Link>
          {me?.client ? <NotificationBell realm="client" className="mr-1 ml-auto" /> : null}
          <button
            ref={menuButton}
            type="button"
            className="rounded-md px-2 py-1 text-sm text-white/80 md:hidden"
            aria-expanded={open}
            aria-controls="main-nav"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? t('common.close') : t('nav.menu')}
          </button>
        </div>
        <nav
          id="main-nav"
          className={`${open ? 'block' : 'hidden'} px-3 pb-4 md:block md:min-h-0 md:flex-1 md:overflow-y-auto`}
          onClick={() => setOpen(false)}
        >
          <ClientNav items={items} signedIn={Boolean(me?.client)} />
          <SiteSidebarPages pages={sitePages.sidebar} />
          {me?.staff ? (
            <div className="mt-4 border-t border-white/10 pt-4">
              <NavItem to="/admin">{t('nav.admin')}</NavItem>
            </div>
          ) : null}
        </nav>
        <div
          className={`${open ? 'flex' : 'hidden'} items-center justify-between gap-2 border-t border-white/10 px-4 py-3 md:flex`}
        >
          {me ? (
            <>
              <Link to="/account" className="min-w-0 truncate font-mono text-xs text-white/70 hover:text-white">
                {me.client?.email ?? me.staff?.email}
              </Link>
              <button type="button" className="text-sm text-white/80 hover:text-white" onClick={onLogout}>
                {t('nav.logout')}
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm text-white/80 hover:text-white">
                {t('nav.login')}
              </Link>
              <Link to="/register" className="text-sm font-semibold text-white hover:underline">
                {t('nav.register')}
              </Link>
            </>
          )}
        </div>
      </aside>
      <div className="flex min-h-screen min-w-0 flex-col">
        {me?.impersonating ? <ImpersonationBanner me={me} /> : null}
        {branding.maintenance && me?.staff ? <MaintenanceBanner /> : null}
        <CookieNotice />
        <SiteHeaderMenu pages={sitePages.header} />
        <main id="main" tabIndex={-1} className="w-full min-w-0 flex-1 px-4 py-8 focus:outline-none md:px-10">
          <Outlet />
        </main>
        <footer className="flex flex-wrap items-end gap-x-4 gap-y-1 px-4 pt-2 pb-4 text-xs text-muted md:px-10">
          <span>
            © {new Date().getFullYear()} {company.name}
          </span>
          <Link to="/accessibility" className="underline-offset-2 hover:text-text hover:underline">
            {t('shell.accessibility')}
          </Link>
          <SiteFooterLinks pages={sitePages.footer} />
          {legal?.privacy_url ? (
            <SiteLink href={legal.privacy_url} className="underline-offset-2 hover:text-text hover:underline">
              {t('shell.privacy')}
            </SiteLink>
          ) : null}
          <LanguagePicker />
          <ThemePicker realm="client" className="sm:ml-auto" />
        </footer>
      </div>
      <BrandColors branding={branding} />
    </div>
  );
}
